import classNames from 'classnames';
import { observer } from 'mobx-react';

import { assertUnreachable } from '../../../app/shared/common';
import ThreeDotsLoader from '../../../app/shared/components/Loaders/ThreeDotsLoader';
import excelFile from '../../../images/pricelists/excelFile.svg';
import spreadsheet from '../../../images/pricelists/spreadsheet.svg';
import { ExportPricelistToExcelModalTranslation } from '../../localization/ModuleTranslation';
import type { DownloadedFile } from '../../model';
import Button from '../../ui/Button/Button';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalHeading } from '../../ui/Modal/Modal';
import IncludeNetPriceCheckbox from '../IncludeNetPriceCheckbox/IncludeNetPriceCheckbox';
import css from './ExportPricelistToExcelModal.css';
import ExportPricelistToExcelModalState, {
  type ExportPricelistToExcelStatus,
} from './ExportPricelistToExcelModalState';

interface ExportPricelistToExcelModalProps {
  state: ExportPricelistToExcelModalState;
  translation: ExportPricelistToExcelModalTranslation;
  trigger: React.ReactNode;
}

const ExportPricelistToExcelModal = observer(({ state, translation, trigger }: ExportPricelistToExcelModalProps) => {
  const { file } = state;

  return (
    <Modal isOpen={state.visible} onOpenChange={state.toggle}>
      {trigger}
      <ModalContent size="medium">
        <ModalHeader closeLabel={translation.closeLabel}>
          <ModalHeading>{translation.heading}</ModalHeading>
        </ModalHeader>
        <div className={css.options}>
          <IncludeNetPriceCheckbox
            isSelected={state.includeNetPrice}
            isAvailable={state.canIncludeNetPrice}
            onChange={state.setIncludeNetPrice}
            translation={translation.includeNetPrice}
          />
        </div>
        <ModalBody className={css.body}>
          <div className={css.file}>
            <img className={css.fileShape} src={excelFile} alt="" />
            <div className={css.fileContent}>
              {state.status === 'pending' ? (
                <ThreeDotsLoader size="medium" />
              ) : (
                <img className={css.spreadsheet} src={spreadsheet} alt="" />
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter className={css.footer}>
          <p role="status" className={classNames(css.status, { [css.error]: state.status === 'error' })}>
            {getStatusMessage(state.status, translation)}
          </p>
          {file ? (
            <Button theme="primary" variant="solid" onClick={() => downloadFile(file)}>
              {translation.downloadLabel}
            </Button>
          ) : (
            <Button theme="primary" variant="solid" isDisabled={state.status === 'pending'} onClick={state.createFile}>
              {translation.exportLabel}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

export default ExportPricelistToExcelModal;

/** Used when the server did not name the file. */
const DEFAULT_FILE_NAME = 'Pricelist.xlsx';

function downloadFile({ content, fileName }: DownloadedFile): void {
  const url = URL.createObjectURL(content);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName ?? DEFAULT_FILE_NAME;
  link.click();

  // Revoking right after `click()` can cancel the download in some browsers; give it a moment to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function getStatusMessage(
  status: ExportPricelistToExcelStatus,
  translation: ExportPricelistToExcelModalTranslation,
): string {
  switch (status) {
    case 'idle':
      return '';
    case 'pending':
      return translation.generatingLabel;
    case 'success':
      return translation.readyLabel;
    case 'error':
      return translation.errorLabel;
    default:
      assertUnreachable(status);
  }
}
